import { LineChart } from "@mantine/charts"
import { Box, Group, HoverCard, Stack, Text } from "@mantine/core"
import { useQuery } from "@tanstack/react-query"
import startCase from "lodash/startCase"
import { useTranslation } from "react-i18next"
import { icons, Score } from "@/components/utils"
import { useBreakpoints } from "@/hooks"
import { categories, type TUser } from "@/types"
import { dayjs, fetchAPIStats } from "@/utils"
import { Block } from "./utils"

type TStatDiff = {
  category?: string
  diff?: number | null
}

type TStatHistory = {
  category?: string
  value?: number | null
  created?: number | string | null
  dtstart?: number | string | null
  date?: number | string | null
  timestamp?: number | string | null
}

function toTimestamp(value: TStatHistory["created"]): number | null {
  if (value === null || value === undefined || value === "") return null

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null
  }

  const parsed = Number(value)
  if (Number.isFinite(parsed)) return parsed

  const date = dayjs(value)
  if (date.isValid()) return date.valueOf()

  return null
}

function getStatTimestamp(stat: TStatHistory) {
  return toTimestamp(stat.created) ?? toTimestamp(stat.timestamp) ?? toTimestamp(stat.dtstart) ?? toTimestamp(stat.date)
}

export function ProfileStats({ user }: { user: TUser }) {
  const xl = { gridColumnEnd: "span 1", gridColumnStart: undefined, gridRowStart: undefined }
  const lg = { gridColumnEnd: "span 1", gridColumnStart: 3, order: 1, gridRowStart: 3 }
  const md = { gridColumnEnd: "span 2" }
  const sx = useBreakpoints({ xl, lg, md, sm: md, xs: {} })

  const { data = [] } = useQuery({
    queryKey: ["stats", user.id],
    queryFn: () =>
      fetchAPIStats({
        where: {
          latest: "user",
          user_id: user.id,
        },
      }),
  })
  if (!data.length) return null

  return (
    <Group style={sx} gap="md" align="flex-start" justify="flex-end">
      {categories.map((cat) => {
        const value = user[cat as keyof TUser]
        if (!value) return null
        const stat = data.find((s: TStatDiff) => s.category === cat)
        return <ProfileStatCard key={cat} category={cat} userId={user.id} value={value as number} stat={stat} />
      })}
    </Group>
  )
}

function ProfileStatCard({
  category,
  userId,
  value,
  stat,
}: {
  category: string
  userId: string
  value: number
  stat?: TStatDiff
}) {
  const { t } = useTranslation()
  const valueLabel = t("Value")
  const CategoryIcon = icons[category]

  const { data: history = [] } = useQuery({
    queryKey: ["stats", "history", userId, category],
    queryFn: () =>
      fetchAPIStats({
        where: {
          user_id: userId,
          category,
        },
        limit: 20,
      }),
  })

  const chartData = history
    .filter((entry: TStatHistory) => entry.value !== null && entry.value !== undefined)
    .map((entry: TStatHistory, index: number) => {
      const timestamp = getStatTimestamp(entry)
      return {
        idx: index,
        label: timestamp ? dayjs(timestamp).format("DD MMM YYYY") : String(index + 1),
        [valueLabel]: Number(entry.value) / 10,
        timestamp,
      }
    })
    .sort((a, b) => {
      if (a.timestamp && b.timestamp) return a.timestamp - b.timestamp
      if (a.timestamp) return -1
      if (b.timestamp) return 1
      return a.idx - b.idx
    })

  return (
    <HoverCard openDelay={150} closeDelay={120} withinPortal position="left" withArrow>
      <HoverCard.Target>
        <Box>
          <Block p="md" w={200} shadow="md" h="auto">
            <Group tt="uppercase" c="dimmed" fw={700} justify="flex-end">
              {t(category)}
            </Group>
            <Group justify="flex-end" align="flex-end" gap="xs" mt={10}>
              {stat?.diff !== null && stat?.diff !== undefined && (
                <Text c={stat.diff === 0 ? "dimmed" : stat.diff > 0 ? "green.7" : "red.7"} fw={500}>
                  {stat.diff === 0 ? "-" : `${stat.diff > 0 ? "+" : ""}${stat.diff / 10}`}
                </Text>
              )}
              <Score value={value} text fz={32} lh={1} />
            </Group>
            {CategoryIcon && (
              <CategoryIcon
                size={26}
                stroke={2}
                style={{
                  position: "absolute",
                  left: 16,
                  bottom: 16,
                  color: "var(--mantine-color-gray-5)",
                }}
              />
            )}
          </Block>
        </Box>
      </HoverCard.Target>
      <HoverCard.Dropdown p="sm">
        <Stack gap="xs">
          <Text fw={600} fz="sm">
            {`${startCase(t(category))}: ${t("History")}`}
          </Text>
          {chartData.length === 0 ? (
            <Text c="dimmed" fz="xs">
              {t("No rating history")}
            </Text>
          ) : (
            <LineChart
              h={140}
              w={280}
              data={chartData}
              dataKey="label"
              series={[{ name: valueLabel, color: "blue.6" }]}
              curveType="natural"
              dotProps={{ r: 3 }}
              gridAxis="xy"
              yAxisProps={{ domain: [0, 10] }}
              withLegend={false}
              withDots
              valueFormatter={(val) => (Number.isFinite(val) ? val.toFixed(1) : "-")}
            />
          )}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  )
}
